import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { NextResponse, NextRequest } from 'next/server';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Counts the number of words in a string.
 * @param text The input string.
 * @returns The number of words.
 */
export function wordCount(text: string): number {
  if (!text) return 0;
  // Simple split by whitespace, filter out empty strings
  return text.trim().split(/\s+/).filter(Boolean).length;
}

/**
 * Calculates the Levenshtein distance between two strings.
 * This measures the minimum number of single-character edits
 * (insertions, deletions or substitutions) required to change one word into the other.
 * @param s1 The first string.
 * @param s2 The second string.
 * @returns The Levenshtein distance.
 */
export function calculateLevenshtein(s1: string, s2: string): number {
  if (s1.length > s2.length) {
    [s1, s2] = [s2, s1]; // Ensure s1 is the shorter string
  }

  const distances = Array(s1.length + 1);
  for (let i = 0; i <= s1.length; i++) {
    distances[i] = i;
  }

  for (let j = 1; j <= s2.length; j++) {
    let previousDiagonal = distances[0];
    distances[0] = j;
    for (let i = 1; i <= s1.length; i++) {
      const oldDiagonal = distances[i];
      const cost = s1[i - 1] === s2[j - 1] ? 0 : 1;
      distances[i] = Math.min(
        distances[i] + 1, // Deletion
        distances[i - 1] + 1, // Insertion
        previousDiagonal + cost // Substitution
      );
      previousDiagonal = oldDiagonal;
    }
  }

  return distances[s1.length];
}

// --- Auth Cookie Helpers ---
const AUTH_COOKIE_NAME = 'fb_id_token';
const COOKIE_OPTIONS = {
  httpOnly: true,
  secure: process.env.NODE_ENV === 'production',
  sameSite: 'lax' as const,
  path: '/',
  maxAge: 60 * 60 * 24 * 7, // 7 days
};

export function setAuthCookie(res: NextResponse, token: string) {
  res.cookies.set(AUTH_COOKIE_NAME, token, COOKIE_OPTIONS);
}

export function clearAuthCookie(res: NextResponse) {
  res.cookies.set(AUTH_COOKIE_NAME, '', { ...COOKIE_OPTIONS, maxAge: 0 });
}

export function getAuthTokenFromRequest(
  reqOrCookies:
    | NextRequest
    | { cookies: { get: (name: string) => { value?: string } | undefined } }
    | { get: (name: string) => { value?: string } | undefined }
): string | null {
  // If it's an object with a 'get' method (cookies() from next/headers)
  if (
    reqOrCookies &&
    typeof reqOrCookies === 'object' &&
    'get' in reqOrCookies &&
    typeof reqOrCookies.get === 'function'
  ) {
    return reqOrCookies.get(AUTH_COOKIE_NAME)?.value || null;
  }
  // If it's an object with a 'cookies' property that has a 'get' method (NextRequest)
  if (
    reqOrCookies &&
    typeof reqOrCookies === 'object' &&
    'cookies' in reqOrCookies &&
    reqOrCookies.cookies &&
    typeof reqOrCookies.cookies.get === 'function'
  ) {
    return reqOrCookies.cookies.get(AUTH_COOKIE_NAME)?.value || null;
  }
  return null;
}
